/**
 * Hub primitives (UI/UX plan §2, ruling D2) — the shared editorial furniture every
 * area HUB composes from: a state-aware HERO header (eyebrow + serif title + lead +
 * ONE state-derived primary CTA) and a plan-rail STATE ROW (mark + label + honest
 * one-line detail + optional count chip + affordance).
 *
 * Pure lit-html render helpers, no VM: a surface resolves its own copy through `t()`
 * and hands STRINGS in (pluralization stays in the surface). Extracted here because
 * ≥2 hubs share each piece — the hero rides every area hub (Workshops · Kompass ·
 * Suche · Bewerbungen); the state row rides the Kompass resources, the Suche
 * readiness list and the Workshops subordinate rows. Nothing speculative: only the
 * two shapes that genuinely recur live here.
 *
 * All emitted `is-*` classes are REGISTERED career states (done/current/open/locked);
 * CTAs carry a REGISTERED `data-act` verb + an optional `data-url` payload, so the
 * host's `actions()` map dispatches them and the stable AX audit stays green.
 */

import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"

/** A single state-derived call to action. `nav` needs `url`; action verbs (start-search …) don't. */
export interface HubCta {
  readonly label: string
  /** A registered `data-act` verb (e.g. `nav`, `start-search`). */
  readonly act: string
  /** The `data-url` nav target (nav CTAs only). */
  readonly url?: string
  /** `ghost` = a quiet secondary affordance (row CTAs default to it); else the prominent brand CTA. */
  readonly tone?: "primary" | "ghost"
  readonly pending?: boolean
  readonly disabled?: boolean
}

/** The editorial hero header — one per hub, one `<h1>`. */
export interface HubHeroOpts {
  readonly eyebrow: string
  readonly title: string
  readonly lead: string
  readonly cta?: HubCta
  /** A short honest state line under the lead (e.g. »Zuletzt: 8 Treffer«). */
  readonly meta?: string
}

/** A resource / stage / readiness row in a hub's plan rail. */
export type HubRowState = "done" | "current" | "open" | "locked"

export interface HubRow {
  readonly label: string
  readonly detail: string
  readonly state: HubRowState
  /** A formatted count chip (already pluralized), when the row carries one. */
  readonly count?: string
  /** The row's trailing affordance (defaults to a ghost tone). */
  readonly cta?: HubCta
  /** An honest German locked reason — replaces the detail line + titles the row (AX-10: gated ≠ broken). */
  readonly lockedReason?: string
}

const ROW_MARK: Record<Exclude<HubRowState, "locked">, string> = { done: "✓", current: "◉", open: "○" }
const LOCK = html`<svg class="hub-row-lock" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 10V8a6 6 0 0 1 12 0v2" /><rect x="4" y="10" width="16" height="10" rx="2" /></svg>`

/** One CTA button — brand-prominent by default, quiet when `ghost`. */
export function hubCta(cta: HubCta): TemplateResult {
  const ghost = cta.tone === "ghost"
  const cls = classMap({ "hub-cta": true, ghost, "is-pending": Boolean(cta.pending) })
  return html`<button
    class=${cls}
    data-act=${cta.act}
    data-url=${cta.url ?? nothing}
    ?disabled=${Boolean(cta.disabled || cta.pending)}
    lang="de"
  >${cta.label}</button>`
}

/** The stable id of a hub hero's `<h1>` — surfaces point `aria-labelledby` at it. */
export const HUB_TITLE_ID = "hub-title"

/** The editorial hero header (Werte-Sektion bar): eyebrow → serif title → lead → one primary CTA. */
export function hubHero({ eyebrow, title, lead, cta, meta }: HubHeroOpts): TemplateResult {
  return html`
    <header class="hub-hero">
      <p class="eyebrow hub-hero-eyebrow" lang="de">${eyebrow}</p>
      <h1 id=${HUB_TITLE_ID} class="surface-title hub-hero-title" lang="de">${title}</h1>
      <p class="surface-lead hub-hero-lead" lang="de">${lead}</p>
      ${meta ? html`<p class="hub-hero-meta" lang="de">${meta}</p>` : nothing}
      ${cta ? html`<div class="hub-cta-bar">${hubCta(cta)}</div>` : nothing}
    </header>
  `
}

/** One plan-rail state row: mark · (label + detail) · optional count · optional affordance. */
export function hubRow(row: HubRow): TemplateResult {
  const locked = row.state === "locked"
  const mark = locked ? LOCK : html`<span aria-hidden="true">${ROW_MARK[row.state]}</span>`
  return html`
    <li class=${classMap({ "hub-row": true, [`is-${row.state}`]: true })} title=${locked && row.lockedReason ? row.lockedReason : nothing}>
      <span class="hub-row-mark">${mark}</span>
      <span class="hub-row-body">
        <span class="hub-row-label" lang="de">${row.label}</span>
        <span class="hub-row-detail" lang="de">${row.lockedReason ?? row.detail}</span>
      </span>
      ${row.count ? html`<span class="hub-row-count chip" lang="de">${row.count}</span>` : nothing}
      ${row.cta ? html`<span class="hub-row-cta">${hubCta({ tone: "ghost", ...row.cta })}</span>` : nothing}
    </li>
  `
}

/** A plan rail of state rows. */
export function hubRows(rows: readonly HubRow[]): TemplateResult {
  return html`<ol class="hub-rows" role="list">${rows.map((r) => hubRow(r))}</ol>`
}
